/**
 * How a lead's facts read on the lead page (third live review, 2026-09-27):
 * phone numbers formatted, and each delivery said as what happened
 * ("Emailed to ops@cascadehomes.com") rather than the raw event
 * ("lead.created → sent · email ops@…").
 */
import type { Destination } from '../api/types';

/** `5555550142` / `+15555550142` → `(555) 555-0142`; anything that is not a 10-digit US number is returned as given. */
export function formatPhone(raw: string): string {
  const digits = raw.replace(/[^\d+]/g, '');
  const m = /^(?:\+?1)?(\d{3})(\d{3})(\d{4})$/.exec(digits);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : raw;
}

/** Event types a delivery can carry, as the update they announce. */
const EVENT_NOUN: Record<string, string> = {
  'lead.created': '',
  'lead.accepted': 'Accepted update',
  'lead.declined': 'Declined update',
  'lead.contacted': 'Contacted update',
  'lead.closed': 'Closed update',
  'lead.reassigned': 'Reassignment update',
  'lead.unmatched': 'Unmatched notice',
  'lead.media_added': 'Added photo',
  'widget.test': 'Test event',
};

function who(dest: Destination | null): { kind: 'email' | 'webhook' | 'sms' | null; target: string | null } {
  if (!dest) return { kind: null, target: null };
  if (dest.kind === 'webhook') {
    const url = typeof dest.config.url === 'string' ? dest.config.url : '';
    try {
      return { kind: 'webhook', target: new URL(url).host };
    } catch {
      return { kind: 'webhook', target: null };
    }
  }
  const to = dest.config.recipients ?? [];
  const target = to.length ? `${to[0]}${to.length > 1 ? ` and ${to.length - 1} more` : ''}` : null;
  return { kind: dest.kind === 'sms' ? 'sms' : 'email', target };
}

/**
 * One sentence for a `delivered` timeline entry. `detail` carries
 * `event_type`, `status` (pending | sent | failed | gave_up), `response_code`,
 * `attempt` and `notice` ('unmatched' when the unmatched notice rode on a
 * `lead.created` delivery, #900).
 */
export function deliverySentence(detail: Record<string, unknown>, dest: Destination | null): string {
  const { kind, target } = who(dest);
  const event = String(detail.event_type ?? '');
  const noun = detail.notice === 'unmatched' ? 'Unmatched notice' : (EVENT_NOUN[event] ?? (event ? event.replace(/^lead\./, '').replace(/[._]/g, ' ') : ''));
  const status = String(detail.status ?? '');
  const attempt = Number(detail.attempt) > 1 ? ` (attempt ${String(detail.attempt)})` : '';
  const code = kind === 'webhook' && detail.response_code ? ` · HTTP ${String(detail.response_code)}` : '';
  const to = target ?? (kind === 'webhook' ? 'your webhook' : kind === 'sms' ? 'your SMS number' : 'your destination');
  const prefix = noun ? `${noun.charAt(0).toUpperCase()}${noun.slice(1)}: ` : '';
  const lower = (text: string) => (prefix ? text.charAt(0).toLowerCase() + text.slice(1) : text);

  let text: string;
  if (status === 'sent') {
    text = kind === 'email' ? `Emailed to ${to}` : kind === 'sms' ? `Texted to ${to}` : kind === 'webhook' ? `Sent to ${to}` : `Delivered to ${to}`;
  } else if (status === 'pending') {
    text = `Sending to ${to}…`;
  } else if (status === 'gave_up') {
    text = `Couldn’t deliver to ${to}; stopped retrying`;
  } else if (status === 'failed') {
    text = `Couldn’t deliver to ${to}; retrying`;
  } else {
    text = `Delivery to ${to}: ${status.replace('_', ' ') || 'unknown'}`;
  }
  return `${prefix}${lower(text)}${attempt}${code}`;
}
